import { useState } from "react";
import {
    ChevronRight,
    IndianRupee,
    Loader2,
    Plus,
    RefreshCw,
    Users
} from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
    createReseller,
    createResellerApiKey,
    getResellerCreditLog,
    getResellers,
    topupResellerCredits,
    type CreditLogEntry,
    type ResellerSummary
} from "@/api/portalApi";
import { useAuth } from "@/auth/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

function fmtInr(n: number) {
    return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n);
}

function fmtDate(s: string) {
    return new Date(s).toLocaleDateString("en-IN");
}

// ── Create reseller dialog ───────────────────────────────────────────────────

function CreateResellerForm({ onDone }: { onDone: () => void }) {
    const { token } = useAuth();
    const qc = useQueryClient();
    const [form, setForm] = useState({ companyName: "", email: "", phone: "", pricingTier: "Standard", notes: "" });
    const [err, setErr] = useState<string | null>(null);

    const mut = useMutation({
        mutationFn: () => createReseller(token, {
            companyName: form.companyName,
            email: form.email,
            phone: form.phone || undefined,
            pricingTier: form.pricingTier,
            notes: form.notes || undefined
        }),
        onSuccess: async () => {
            await qc.invalidateQueries({ queryKey: ["resellers"] });
            onDone();
        },
        onError: e => setErr(e instanceof Error ? e.message : "Failed to create reseller.")
    });

    return (
        <div className="space-y-4">
            <h3 className="text-base font-semibold">New Reseller</h3>
            <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                    <Label>Company name *</Label>
                    <Input value={form.companyName} onChange={e => setForm(f => ({ ...f, companyName: e.target.value }))} />
                </div>
                <div className="space-y-1">
                    <Label>Email *</Label>
                    <Input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
                </div>
                <div className="space-y-1">
                    <Label>Phone</Label>
                    <Input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} />
                </div>
                <div className="space-y-1">
                    <Label>Pricing tier</Label>
                    <select
                        value={form.pricingTier}
                        onChange={e => setForm(f => ({ ...f, pricingTier: e.target.value }))}
                        className="w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    >
                        <option>Standard</option>
                        <option>Premium</option>
                    </select>
                </div>
                <div className="col-span-2 space-y-1">
                    <Label>Notes</Label>
                    <Input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
                </div>
            </div>
            {err && <p className="text-xs text-red-600">{err}</p>}
            <div className="flex gap-2">
                <Button onClick={() => mut.mutate()} disabled={mut.isPending || !form.companyName || !form.email}>
                    {mut.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                    Create
                </Button>
                <Button variant="outline" onClick={onDone}>Cancel</Button>
            </div>
        </div>
    );
}

// ── Reseller detail panel ────────────────────────────────────────────────────

function ResellerDetail({ reseller, onClose }: { reseller: ResellerSummary; onClose: () => void }) {
    const { token } = useAuth();
    const qc = useQueryClient();
    const [topupAmount, setTopupAmount] = useState("");
    const [topupDesc, setTopupDesc] = useState("");
    const [keyName, setKeyName] = useState("");
    const [newKey, setNewKey] = useState<string | null>(null);
    const [err, setErr] = useState<string | null>(null);

    const logQ = useQuery({
        queryKey: ["credit-log", reseller.id],
        queryFn: () => getResellerCreditLog(token, reseller.id)
    });

    const topupMut = useMutation({
        mutationFn: () => topupResellerCredits(token, reseller.id, parseFloat(topupAmount), topupDesc || undefined),
        onSuccess: async () => {
            await qc.invalidateQueries({ queryKey: ["resellers"] });
            await qc.invalidateQueries({ queryKey: ["credit-log", reseller.id] });
            setTopupAmount("");
            setTopupDesc("");
        },
        onError: e => setErr(e instanceof Error ? e.message : "Topup failed.")
    });

    const keyMut = useMutation({
        mutationFn: () => createResellerApiKey(token, reseller.id, keyName),
        onSuccess: data => {
            setNewKey(data.apiKey);
            setKeyName("");
        },
        onError: e => setErr(e instanceof Error ? e.message : "Key creation failed.")
    });

    const txTypeColor: Record<string, string> = {
        Topup: "text-green-600 dark:text-green-400",
        Deduction: "text-red-600 dark:text-red-400",
        Refund: "text-blue-600 dark:text-blue-400",
        AdminAdjustment: "text-amber-600 dark:text-amber-400"
    };

    return (
        <div className="space-y-6">
            <div className="flex items-start justify-between">
                <div>
                    <h3 className="text-lg font-semibold">{reseller.companyName}</h3>
                    <p className="text-sm text-muted-foreground">{reseller.email} · {reseller.pricingTier}</p>
                </div>
                <Button variant="ghost" size="sm" onClick={onClose}>✕</Button>
            </div>

            {/* Balance card */}
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
                {[
                    { label: "Credit Balance", value: fmtInr(reseller.creditBalance), highlight: true },
                ].map(({ label, value, highlight }) => (
                    <div key={label} className={cn(
                        "rounded-lg border p-4",
                        highlight && reseller.creditBalance < 500 ? "border-red-300 bg-red-50 dark:border-red-800 dark:bg-red-900/20" : ""
                    )}>
                        <p className="text-xs text-muted-foreground">{label}</p>
                        <p className="mt-1 text-xl font-bold">{value}</p>
                    </div>
                ))}
            </div>

            {err && <p className="text-xs text-red-600">{err}</p>}

            {/* Topup form */}
            <div className="rounded-lg border p-4 space-y-3">
                <h4 className="text-sm font-semibold">Add Credits</h4>
                <div className="flex gap-2">
                    <Input
                        type="number"
                        min={1}
                        placeholder="Amount (INR)"
                        value={topupAmount}
                        onChange={e => setTopupAmount(e.target.value)}
                        className="w-40"
                    />
                    <Input
                        placeholder="Description (optional)"
                        value={topupDesc}
                        onChange={e => setTopupDesc(e.target.value)}
                    />
                    <Button
                        onClick={() => topupMut.mutate()}
                        disabled={topupMut.isPending || !topupAmount || parseFloat(topupAmount) <= 0}
                    >
                        {topupMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <IndianRupee className="h-4 w-4" />}
                        Top up
                    </Button>
                </div>
            </div>

            {/* Generate API key */}
            <div className="rounded-lg border p-4 space-y-3">
                <h4 className="text-sm font-semibold">Generate API Key</h4>
                <div className="flex gap-2">
                    <Input
                        placeholder="Key name (e.g. WHMCS Production)"
                        value={keyName}
                        onChange={e => setKeyName(e.target.value)}
                    />
                    <Button
                        onClick={() => keyMut.mutate()}
                        disabled={keyMut.isPending || !keyName}
                        variant="outline"
                    >
                        {keyMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                        Create key
                    </Button>
                </div>
                {newKey && (
                    <div className="rounded-md bg-amber-50 border border-amber-200 p-3 dark:bg-amber-900/20 dark:border-amber-800">
                        <p className="text-xs font-semibold text-amber-800 dark:text-amber-400">
                            Copy this key now — it will not be shown again.
                        </p>
                        <code className="mt-1 block select-all break-all rounded bg-white px-2 py-1 text-xs font-mono dark:bg-gray-900">
                            {newKey}
                        </code>
                    </div>
                )}
            </div>

            {/* Credit log */}
            <div>
                <h4 className="text-sm font-semibold mb-3">Credit History</h4>
                {logQ.isLoading ? (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <RefreshCw className="h-4 w-4 animate-spin" /> Loading…
                    </div>
                ) : (
                    <div className="rounded-lg border overflow-hidden">
                        <table className="w-full text-sm">
                            <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
                                <tr>
                                    <th className="px-4 py-2 text-left">Date</th>
                                    <th className="px-4 py-2 text-left">Type</th>
                                    <th className="px-4 py-2 text-right">Amount</th>
                                    <th className="px-4 py-2 text-right">Balance after</th>
                                    <th className="px-4 py-2 text-left">Description</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y">
                                {(logQ.data?.items ?? []).map((entry: CreditLogEntry) => (
                                    <tr key={entry.id} className="hover:bg-muted/20">
                                        <td className="px-4 py-2 whitespace-nowrap">{fmtDate(entry.createdAt)}</td>
                                        <td className={cn("px-4 py-2 font-medium", txTypeColor[entry.type] ?? "")}>{entry.type}</td>
                                        <td className="px-4 py-2 text-right">{fmtInr(entry.amount)}</td>
                                        <td className="px-4 py-2 text-right">{fmtInr(entry.balanceAfter)}</td>
                                        <td className="px-4 py-2 text-muted-foreground truncate max-w-xs">{entry.description ?? "—"}</td>
                                    </tr>
                                ))}
                                {!logQ.data?.items.length && (
                                    <tr>
                                        <td colSpan={5} className="px-4 py-6 text-center text-muted-foreground">No transactions yet.</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export function ResellersPage() {
    const { token } = useAuth();
    const [showCreate, setShowCreate] = useState(false);
    const [selected, setSelected] = useState<ResellerSummary | null>(null);

    const q = useQuery({
        queryKey: ["resellers"],
        queryFn: () => getResellers(token)
    });

    if (selected) {
        return (
            <div className="mx-auto max-w-4xl">
                <ResellerDetail reseller={selected} onClose={() => setSelected(null)} />
            </div>
        );
    }

    return (
        <div className="mx-auto max-w-4xl space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-2xl font-bold">Resellers</h2>
                    <p className="mt-1 text-sm text-muted-foreground">
                        Manage reseller accounts, assign credits, and issue API keys.
                    </p>
                </div>
                <Button onClick={() => setShowCreate(true)}>
                    <Plus className="mr-2 h-4 w-4" /> Add Reseller
                </Button>
            </div>

            {showCreate && (
                <div className="rounded-xl border bg-card p-6">
                    <CreateResellerForm onDone={() => setShowCreate(false)} />
                </div>
            )}

            {q.isLoading ? (
                <div className="flex items-center justify-center py-16 text-muted-foreground">
                    <RefreshCw className="mr-2 h-4 w-4 animate-spin" /> Loading…
                </div>
            ) : q.error ? (
                <p className="text-sm text-red-600">Failed to load resellers.</p>
            ) : (
                <div className="rounded-xl border overflow-hidden">
                    <table className="w-full text-sm">
                        <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
                            <tr>
                                <th className="px-4 py-3 text-left">Company</th>
                                <th className="px-4 py-3 text-left">Email</th>
                                <th className="px-4 py-3 text-left">Tier</th>
                                <th className="px-4 py-3 text-right">Credit Balance</th>
                                <th className="px-4 py-3 text-left">Status</th>
                                <th className="px-4 py-3 text-left">Joined</th>
                                <th className="px-4 py-3" />
                            </tr>
                        </thead>
                        <tbody className="divide-y">
                            {(q.data ?? []).map((r: ResellerSummary) => (
                                <tr key={r.id} className="hover:bg-muted/20 cursor-pointer" onClick={() => setSelected(r)}>
                                    <td className="px-4 py-3 font-medium">{r.companyName}</td>
                                    <td className="px-4 py-3 text-muted-foreground">{r.email}</td>
                                    <td className="px-4 py-3">{r.pricingTier}</td>
                                    <td className={cn(
                                        "px-4 py-3 text-right font-mono font-semibold",
                                        r.creditBalance < 500 ? "text-red-600 dark:text-red-400" : "text-green-700 dark:text-green-400"
                                    )}>
                                        {fmtInr(r.creditBalance)}
                                    </td>
                                    <td className="px-4 py-3">
                                        <span className={cn(
                                            "rounded-full px-2 py-0.5 text-xs font-medium",
                                            r.isActive
                                                ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
                                                : "bg-gray-100 text-gray-600 dark:bg-gray-800"
                                        )}>
                                            {r.isActive ? "Active" : "Inactive"}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3 text-muted-foreground">{fmtDate(r.createdAt)}</td>
                                    <td className="px-4 py-3">
                                        <ChevronRight className="h-4 w-4 text-muted-foreground" />
                                    </td>
                                </tr>
                            ))}
                            {!q.data?.length && (
                                <tr>
                                    <td colSpan={7} className="py-12 text-center">
                                        <Users className="mx-auto mb-2 h-8 w-8 text-muted-foreground/40" />
                                        <p className="text-muted-foreground">No resellers yet.</p>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
